import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCw } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import { ToggleGroup, ToggleItem } from '../../ui/toggle';
import { cn } from '../../../lib/utils';
import { CreatedRange, ManagerFilter } from './IssuedFilterControls';
import { searchWithStatsFilters, searchWithStatsPatch, STATS_STATUS_FILTERS, statsFiltersFromSearch, statsQuery } from './statsFilters';
import { formatUSD, METRICS, share, totalLabel } from './statsView';
import { ManagerChart, StatTile, TimeChart, TopPlayers } from './StatsCharts';

function Tiles({ totals, status }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile label={totalLabel(status)} value={formatUSD(totals.usd)} sub={`${totals.count.toLocaleString('en-US')} comps`} />
      <StatTile label="Players" value={totals.players.toLocaleString('en-US')}
        sub={`${formatUSD(totals.players ? totals.usd / totals.players : 0)} per player`} />
      <StatTile label="Average comp" value={formatUSD(totals.count ? totals.usd / totals.count : 0)} sub="USD at the issue-time rate" />
      <StatTile label="Overrides" value={totals.override_count.toLocaleString('en-US')}
        sub={`${formatUSD(totals.override_usd)} · ${share(totals.override_usd, totals.usd)} of USD`} />
    </div>
  );
}

// IssuedStats charts the comps saved in bi_backoffice.vip_comp_bonuses_hist: totals, per day,
// per manager and the top players, in USD at the issue-time rate. The filters live in the URL
// (see statsFilters), so a filtered view can be linked.
export default function IssuedStats({ onOpenPlayer }) {
  const { activeBrand } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, from, to, manager } = statsFiltersFromSearch(searchParams);
  const [metric, setMetric] = useState('usd');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setFilters = (patch) => {
    const next = searchWithStatsPatch(searchParams, patch);
    if (next.toString() !== searchParams.toString()) setSearchParams(next);
  };

  // The link always says which status it shows.
  useEffect(() => {
    if (!searchParams.has('status')) setSearchParams((sp) => searchWithStatsFilters(sp, statsFiltersFromSearch(sp)), { replace: true });
  }, [searchParams, setSearchParams]);

  // Only the latest request may update the charts (filters and brand can change mid-flight).
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await vipAPI.getIssuedBonusStats(statsQuery({ status, from, to, manager }));
      if (requestId === requestIdRef.current) setData(res.data);
    } catch (e) {
      // drop the previous figures: they belong to other filters
      if (requestId === requestIdRef.current) {
        setData(null);
        setError(e?.response?.data?.error || 'Loading the bonus stats failed.');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [status, from, to, manager]);

  useEffect(() => { load(); }, [load, activeBrand]);

  const totals = data?.totals;
  return (
    <div className="space-y-3">
      <Card className="flex flex-wrap items-center gap-2 p-4">
        {STATS_STATUS_FILTERS.map((f) => (
          <button
            key={f.key} type="button" aria-pressed={status === f.key} onClick={() => setFilters({ status: f.key })}
            className={cn('rounded-full px-3 py-1.5 text-xs font-bold',
              status === f.key ? 'bg-ink text-surface' : 'bg-surface-2 text-ink-dim hover:text-ink')}
          >
            {f.label}
          </button>
        ))}
        <span className="flex-1" />
        <CreatedRange from={from} to={to} onChange={setFilters} />
        <ManagerFilter value={manager} managers={data?.managers} onChange={(m) => setFilters({ manager: m })} />
        <ToggleGroup value={metric} onChange={setMetric}>
          {METRICS.map((m) => <ToggleItem key={m.key} value={m.key}>{m.label}</ToggleItem>)}
        </ToggleGroup>
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={load} disabled={loading}><RotateCw className="h-3.5 w-3.5" /> Refresh</Button>
      </Card>

      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading && !data && <div className="flex justify-center py-10"><Spinner /></div>}
      {totals && totals.count === 0 && (
        <Card className="p-6 text-center text-xs font-medium text-ink-mute">No bonuses match.</Card>
      )}
      {totals && totals.count > 0 && (
        <div className={cn('space-y-3', loading && 'opacity-60')}>
          <Tiles totals={totals} status={status} />
          {totals.unpriced > 0 && (
            <Alert variant="warning">
              <AlertDescription>
                {totals.unpriced} comp{totals.unpriced === 1 ? ' has' : 's have'} no USD rate for the day it was added and
                {totals.unpriced === 1 ? ' is' : ' are'} counted without a USD value.
              </AlertDescription>
            </Alert>
          )}
          <TimeChart days={data.days} metric={metric} status={status} from={from} to={to} />
          <div className="grid gap-3 lg:grid-cols-2">
            <ManagerChart managers={data.by_manager} metric={metric} />
            <TopPlayers
              players={(metric === 'count' ? data.top_players_by_count : data.top_players) || []}
              totals={totals} metric={metric} onOpenPlayer={onOpenPlayer}
            />
          </div>
        </div>
      )}
    </div>
  );
}
